// Auto-discovers every class record under ./records/*.json at build time via
// Vite's import.meta.glob, keyed by its `id` field (uppercased). To add a
// new class, drop a new .json file in ./records — nothing here needs to
// change, and no new route needs to be declared in App.jsx. Records are
// plain data (no code), so this stays safe to hand off to a non-developer.
const modules = import.meta.glob('./records/*.json', { eager: true });

export const classRegistry = {};

for (const path in modules) {
  const record = modules[path].default;
  if (record && record.id) {
    classRegistry[record.id.toUpperCase()] = record;
  }
}

export function getClassRecord(classId) {
  if (!classId) return undefined;
  return classRegistry[classId.toUpperCase()];
}

// A Record ID at /verify can hold BOTH credential types at once (a
// certificate holder completed the class too, so they still have the
// diploma for it) — see "Diplomas vs Certificates" in CLAUDE.md before
// changing the rules below:
//
// - "certificate": a participant tagged with `certificateSlug` +
//   `certificationProject` (today, only Anna and Jimmy in GB250814). Earned
//   by completing a real improvement project on top of the class, not just
//   attending it. Always requires a `name` on the data side (needed for the
//   private roster later) even though that name is never returned here.
// - "diploma": proof of curriculum completion, granted independently of the
//   certificate to anyone who attended at least 60% of a class (the same
//   passing bar used for the pct-high/pct-low coloring in ClassRecord.jsx
//   and CertificateTypeRecord.jsx). Below that bar, the class isn't
//   considered completed, so no diploma exists for that Record ID.
//
// Returns `{ recordId, hrs, pct, classId, instructor, dateRange,
// credentials: [...] }` where `credentials` holds one entry per type that
// applies (certificate first, since it's the more significant one, then
// diploma), 0, 1, or 2 entries. Returns undefined only when NEITHER applies
// (Record ID not found at all, or found but below the attendance bar with
// no certificate) — "no credential found".
//
// PRIVACY: this function is called from public, unauthenticated pages
// (/verify, /verify/:recordId). It must never return a participant's name,
// or any other personally identifying field. See "Public page privacy" in
// CLAUDE.md before changing this.
export function getCredential(recordId) {
  if (!recordId) return undefined;
  const q = recordId.toUpperCase();

  for (const classRecord of Object.values(classRegistry)) {
    const participant = classRecord.participants.find((p) => p.id.toUpperCase() === q);
    if (!participant) continue;

    const credentials = [];

    if (participant.name && participant.certificateSlug && participant.certificationProject) {
      credentials.push({
        type: 'certificate',
        programName: classRecord.programName,
        certificationProject: participant.certificationProject,
      });
    }

    if (parseInt(participant.pct) >= 60) {
      credentials.push({ type: 'diploma', programName: classRecord.programName });
    }

    if (credentials.length === 0) return undefined;

    return {
      recordId: participant.id,
      hrs: participant.hrs,
      pct: participant.pct,
      classId: classRecord.id,
      instructor: classRecord.instructor,
      dateRange: classRecord.dateRange,
      credentials,
    };
  }

  return undefined;
}
